<script setup lang="ts">
import { formatNumber } from "chartjs-graph";
import CompareTooltip from "@/modules/explorer/components/Metrics/Table/CompareTooltip.vue";
import { Cell } from "@tanstack/table-core";
import {
	createMetricObject,
	TanstackCellContext,
} from "@/modules/explorer/components/Metrics/MetricHelper";
import { computed, inject, onBeforeMount, ref } from "vue";
import { derivedDocumentKey } from "@/modules/explorer/ExplorerUtils";
import { DataTableMetric } from "@/modules/explorer/components/Metrics/MetricResponseProcessor";
import { orderComparisonData } from "chartjs-graph";
import DeltaChip from "@/modules/explorer/components/Metrics/Table/DeltaChip.vue";

const derivedDocument = inject(derivedDocumentKey);
const { cell, value, context } = defineProps<{
	cell: Cell<Record<string, string>, any>;
	value: number;
	context: TanstackCellContext;
}>();

const hasNonDefaultComparison = context.comparisonPeriods.some(
	(p) =>
		p !== derivedDocument.value.date_comparison.comparisonLabel && p !== "Bud"
);

const allowedBudgetComparisonVerticals = [
	"revenuesdriveout",
	"revenuesdrivein",
	"advertising_revenue_distr",
];

let showComparison =
	context.dataTable.is_comparison_nested &&
	context.comparisonPeriods.length > 1;

if (
	context.isBudgetDeltaEnabled &&
	!allowedBudgetComparisonVerticals.includes(context.metric.name_clean) &&
	!hasNonDefaultComparison
) {
	showComparison = false;
}

const metricObject = ref<DataTableMetric | null>(null);

const comparisonMetric = computed(() => {
	if (!metricObject.value) return null;

	return metricObject.value.values[1];
});

const isRateCard = computed(() => context.metric.format === "rate_card");

const showAbsolute = computed(
	() => context.dataTable.show_change_absolute && !isRateCard.value
);

const showRelative = computed(
	() => context.dataTable.show_change_relative_to_current && !isRateCard.value
);

onBeforeMount(() => {
	if (!showComparison) return;

	let comparisonPeriods: string[];

	if (
		derivedDocument.value.date_comparison.customPeriods &&
		derivedDocument.value.date_comparison.customPeriods.length
	) {
		const comparisonData =
			derivedDocument.value.date_comparison.customPeriods.map(
				(period) => period.name
			);

		comparisonData.push(derivedDocument.value.date_comparison.comparisonLabel);
		if (context.isBudgetDeltaEnabled) {
			comparisonData.push("Bud");
		}

		comparisonPeriods = orderComparisonData(
			derivedDocument.value,
			comparisonData,
			(v) => v
		);
	} else {
		// fallback: Y-1, Y-3, etc → normalize to same shape
		comparisonPeriods = orderComparisonData(
			derivedDocument.value,
			context.comparisonPeriods,
			(v) => v
		);
	}

	metricObject.value = createMetricObject(
		context.metric,
		cell.row.original,
		comparisonPeriods,
		derivedDocument.value.date_comparison.comparisonLabel
	);
});
</script>

<template>
	<compare-tooltip
		:metric="metricObject"
		:show-change-absolute="showAbsolute"
		:show-change-relative-to-current="showRelative"
	>
		<div class="d-flex flex-row justify-space-between ga-2 delta-td">
			<div class="value-row">
				{{ formatNumber(value, context.metric.format) }}
			</div>
			<div v-if="showComparison">
				<div
					class="delta-row"
					v-if="comparisonMetric && (showAbsolute || showRelative)"
				>
					<DeltaChip
						:delta-absolute="comparisonMetric.deltaAbsolute"
						:delta-relative="comparisonMetric.deltaRelative"
						:show-absolute="showAbsolute"
						:show-relative="showRelative"
						:format="context.metric.format"
					/>
				</div>
			</div>
			<div v-else-if="comparisonMetric" class="compare-row">
				{{ formatNumber(comparisonMetric.totalValue, context.metric.format) }}
			</div>
		</div>
	</compare-tooltip>
</template>

<style scoped lang="scss">
.delta-td {
	max-width: 200px;
	// stay inside the virtualized row height defined by TenStackTable
	max-height: var(--table-row-h, 26px);
}

.value-row {
	display: flex;
	align-items: center;
	justify-content: end;
	line-height: 1;
}

.delta-row,
.compare-row {
	display: flex;
	align-items: center;
	justify-content: end;
	gap: 4px;
	font-size: 10px;
	line-height: 1;
	height: 15px;
	white-space: nowrap;
	color: rgba(var(--color-on-surface), 0.66);
}
</style>
